import React, { useState } from 'react';
import moment from 'moment/moment';
import { BmoTransactionsTableContext, IBmoTransactionsTableContext } from './BmoTransactionsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { getTransactions } from '../../api';
import { IBmoTransaction } from '@shared/api';
import { useDrawer } from '@features/Modal';
import { BmoTransactionsViewDrawer } from '../../components';
import { usePagination } from '@features/Pagination';
import { combineFilterWithOp, joinFiltersWithComma } from '@shared/utils';

export const BmoTransactionsTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();
    const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
    const [searchString, setSearchString] = useState<string>('');
    const [bmoDebitCreditStatus, setBmoDebitCreditStatus] = useState<string>('');
    const [bmoReconciledStatus, setBmoReconciledStatus] = useState<string>('');
    const [transactionTypes, setTransactionTypes] = useState<string[]>([]);

    const {
        data: bmoTransactions,
        loading: bmoTransactionsLoading,
        error: bmoTransactionsError,
        handleRefresh: bmoTransactionsRefresh,
    } = useAsyncData(
        async () => {
            const filter = {
                cdDbtInd: { value: String(bmoDebitCreditStatus), operator: '=' },
                type: { value: String(transactionTypes), operator: '=' },
                amt: { value: searchString, operator: '=*' },
                isReconciled: { value: bmoReconciledStatus, operator: ' #In' },
                from: {
                    keyOverride: 'txDtTm',
                    value: date?.length && moment(date).startOf('day').format(),
                    operator: '>=',
                },
                to: {
                    keyOverride: 'txDtTm',
                    value: date?.length && moment(date).endOf('day').format(),
                    operator: '<=',
                },
            };
            const query = {
                page,
                pageSize,
                // filter: joinFiltersWithComma(combineFilterWithOp(filter), combineFieldsFilter(filterKeys)),
                filter: joinFiltersWithComma(combineFilterWithOp(filter)),
            };
            const { items, totalResults } = await getTransactions(query);
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [date, page, pageSize, transactionTypes, bmoReconciledStatus, bmoDebitCreditStatus, searchString],
    );

    useErrorNotify(bmoTransactionsError, 'Transactions');

    const { open: openBmoTransactionsViewDrawer } = useDrawer(BmoTransactionsViewDrawer);

    const onBmoTransactionViewClick = (bmoTransaction: IBmoTransaction) => {
        openBmoTransactionsViewDrawer({ bmoTransaction, bmoTransactionsRefresh });
    };

    const value: IBmoTransactionsTableContext = {
        date,
        bmoTransactions,
        bmoTransactionsLoading,
        paginationOptions,
        transactionTypes,
        bmoReconciledStatus,
        bmoDebitCreditStatus,
        onBmoTransactionViewClick,
        setBmoReconciledStatus,
        setBmoDebitCreditStatus,
        setDate,
        setSearchString,
        setTransactionTypes,
    };

    return <BmoTransactionsTableContext.Provider value={value}>{children}</BmoTransactionsTableContext.Provider>;
};
